import { useMemo } from 'react';
import { bg, type MoneyCents } from '@tragfaehigkeit/core';
import { de } from '../../../lib/i18n/de';
import { formatCentsForDisplay } from '../../../components/finance/MoneyInput';
import { useWizardAudience } from '../../../components/wizard/WizardAudienceContext';
import type { ResourcesDraft } from '../../../lib/store/wizard';
import { selectBruttoForBgLuecke } from '../../../lib/store/scoreInput';

interface LueckeStepProps {
  resources: ResourcesDraft;
}

/** Sub-Step 4.5: Tragfähigkeitslücke (automatisch berechnet, editorial-Anzeige). */
export function LueckeStep({ resources }: LueckeStepProps) {
  const t = de.steps.resources.subSteps.luecke;
  const audience = useWizardAudience();

  const breakdown = useMemo(() => {
    if (resources.bgComposition.length === 0) return null;
    // Brutto-Erwerbseinkommen (§ 11 SGB II): bevorzugt das first-class
    // Schema-Feld, sonst Fallback auf Profit-Approximation. Der Adapter
    // loggt eine Warnung in der Console, wenn der Schätz-Pfad genutzt wird.
    const brutto = selectBruttoForBgLuecke(resources, resources.bruttoErwerbseinkommenCents);
    try {
      return bg.computeTragfaehigkeitsLuecke({
        bg: {
          members: resources.bgComposition.map((m) => ({
            role: m.role,
            age: m.age,
            specialNeeds: [...m.specialNeeds],
          })),
          kdu: {
            kaltmiete: resources.kdu.kaltCents as MoneyCents,
            nebenkosten: resources.kdu.nebenCents as MoneyCents,
            heizkosten: resources.kdu.heizCents as MoneyCents,
          },
        },
        bruttoErwerbseinkommenCents: brutto.valueCents,
        ...(resources.sonstigesEinkommenCents !== null
          ? { sonstigesEinkommenCents: resources.sonstigesEinkommenCents as MoneyCents }
          : {}),
      });
    } catch {
      return null;
    }
  }, [resources]);

  if (!breakdown) {
    return (
      <p className="font-serif italic text-stone-500">
        Bitte erfassen Sie zunächst die Bedarfsgemeinschaft (Schritt 4.4), damit die Lücke berechnet
        werden kann.
      </p>
    );
  }

  const luckeCents = breakdown.luckeCents as number;
  const isCovered = luckeCents <= 0;
  const absLueckeCents = Math.abs(luckeCents);

  // Mandanten-Sicht: keine alarmierende Hero-Zahl und kein wertendes Fazit.
  // Die eigenen Zahlen bleiben sachlich sichtbar (Transparenz), die Bewertung
  // liegt beim Berater (Art. 22 DSGVO).
  if (audience === 'mandant') {
    return (
      <div className="space-y-8">
        <p className="max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          {t.mandantIntro}
        </p>
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {t.mandantOverviewLabel}
          </p>
          <dl className="mt-4 grid grid-cols-1 gap-x-10 gap-y-4 border-t border-stone-200 pt-6 md:grid-cols-2">
            <BreakdownRow
              label={t.bedarfLine}
              value={`${formatCentsForDisplay(breakdown.bedarf.totalBedarfCents)} €`}
            />
            <BreakdownRow
              label={t.einkommenLine}
              value={`${formatCentsForDisplay(breakdown.einkommen.anzurechnendesEinkommenCents)} €`}
            />
          </dl>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <p className="max-w-prose font-serif text-base italic text-stone-500 md:text-lg">{t.intro}</p>

      {/* Hero-Zahl */}
      <div>
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {t.gapLabel}
        </p>
        <div className="mt-3 flex items-baseline gap-3">
          <span
            className={`font-serif text-6xl font-black tabular-nums tracking-tight md:text-7xl ${
              isCovered ? 'text-emerald-700' : 'text-stone-900'
            }`}
            data-testid="luecke-value"
          >
            {isCovered ? '0,00' : formatCentsForDisplay(absLueckeCents)}
          </span>
          <span className="font-serif text-2xl text-stone-500">€</span>
        </div>
        <p
          className={`mt-3 max-w-prose font-serif text-base italic ${
            isCovered ? 'text-emerald-700' : 'text-stone-600'
          }`}
        >
          {isCovered ? t.gapHintCovered : t.gapHintUncovered}
        </p>
      </div>

      {/* Breakdown */}
      <dl className="grid grid-cols-1 gap-x-10 gap-y-4 border-t border-stone-200 pt-6 md:grid-cols-2">
        <BreakdownRow
          label={t.bedarfLine}
          value={`${formatCentsForDisplay(breakdown.bedarf.totalBedarfCents)} €`}
        />
        <BreakdownRow
          label={t.einkommenLine}
          value={`${formatCentsForDisplay(breakdown.einkommen.anzurechnendesEinkommenCents)} €`}
        />
        <BreakdownRow
          label={t.gapLine}
          value={`${isCovered ? '+' : ''}${formatCentsForDisplay(absLueckeCents)} €`}
          emphasis
        />
      </dl>
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {label}
      </dt>
      <dd
        className={`mt-1 font-serif tabular-nums tracking-tight ${
          emphasis ? 'text-2xl font-black text-stone-900' : 'text-xl font-bold text-stone-700'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
